// Exports via de dispatcher: ledenexport (person.export) en volledige export
// van de organisatie (data.export).
//
// Aanvragen maakt een voorstel. Is vierogen niet nodig, dan staat de export
// meteen klaar; anders na goedkeuring. De database levert de inhoud één keer,
// binnen 24 uur, alleen aan wie hem aanvroeg (public.export_ophalen). Deze
// module maakt daar de zip van.
import { getClient } from '../data/supabase.js';
import { state } from '../state.js';
import { heeft } from '../router.js';
import { t, locale } from '../lib/i18n.js';
import { esc } from '../lib/format.js';
import { toast } from '../ui/toast.js';
import { vraagReden } from '../ui/reden.js';
import { bouwExport, bestandsnaam } from '../lib/export-leden.js';
import { dispatchFout, vernieuwTeller } from './goedkeuringen.js';

const sb = () => getClient();
const wanneer = iso => iso ? new Date(iso).toLocaleString(locale(), { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }) : '';

export function exportFout(e) {
  const m = String(e?.message || e || '').match(/export:([a-z_]+)/);
  return m ? t('export.fout.' + m[1]) : dispatchFout(e);
}

// Een export aanvragen. soort: 'leden' of 'volledig'.
export async function aanvragen(soort, { paspoorten = false } = {}) {
  const r = await vraagReden({
    titel: t(soort === 'leden' ? 'export.titel' : 'export.volledig.titel'),
    uitleg: t('export.aanvraag.uitleg'),
    knop: t('export.aanvragen'),
    voorbeeld: t('reden.voorbeeld.export'),
  });
  if (!r) return null;
  const { data, error } = await sb().rpc('dispatch', {
    p_handeling: soort === 'leden' ? 'person.export' : 'data.export',
    p_gegevens: { organisation_id: state.currentOrgId, paspoorten: !!paspoorten },
    p_reden: r.reden,
  });
  if (error) { toast(exportFout(error), 'error'); return null; }
  await vernieuwTeller();
  if (data?.status === 'wacht') { toast(t('export.wacht'), 'success'); return 'wacht'; }
  const bon = data?.resultaat?.export;
  if (bon) await ophalen(bon);
  return 'klaar';
}

// De export ophalen en als zip aanbieden.
export async function ophalen(bon) {
  const { data, error } = await sb().rpc('export_ophalen', { p_bon: bon });
  if (error) { toast(exportFout(error), 'error'); return false; }
  const tabellen = { ...(data?.tabellen || {}) };
  // Eén organisatierij hoort als lijst in de zip, net als de rest.
  if (tabellen.organisation && !Array.isArray(tabellen.organisation)) tabellen.organisation = [tabellen.organisation];
  for (const [k, v] of Object.entries(tabellen)) if (v == null) delete tabellen[k];
  const org = state.currentOrg || { id: state.currentOrgId };
  const { zip } = bouwExport({ organisatie: org, gemaaktDoor: state.currentUserEmail, tabellen, soort: data?.soort });
  const a = document.createElement('a');
  a.href = URL.createObjectURL(new Blob([zip], { type: 'application/zip' }));
  a.download = bestandsnaam(org, new Date(), data?.soort === 'volledig' ? 'export' : 'leden');
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
  toast(t('export.klaar'), 'success');
  return true;
}

// Exports die klaarstaan voor deze gebruiker.
export async function klaarstaand(soort) {
  const { data, error } = await sb().from('export_grants').select('*')
    .eq('organisation_id', state.currentOrgId).is('opgehaald_at', null).order('created_at', { ascending: false });
  if (error) return [];
  const nu = Date.now();
  return (data || []).filter(b => (!soort || b.soort === soort) && new Date(b.expires_at).getTime() > nu);
}

export function klaarstaandHtml(lijst) {
  if (!lijst.length) return '';
  return `<div class="banner banner-green" style="margin-top:10px"><i class="ti ti-download"></i><span>${esc(t('export.klaarstaand'))}</span></div>
    ${lijst.map(b => `<div class="org-row"><div style="flex:1;font-size:13px">${esc(t('export.soort.' + b.soort))}
        <span class="start-sub">${esc(t('export.geldig', { wanneer: wanneer(b.expires_at) }))} &middot; &ldquo;${esc(b.reden)}&rdquo;</span></div>
      <button type="button" class="btn btn-primary btn-sm" data-export="ophalen" data-id="${b.id}"><i class="ti ti-download"></i> ${esc(t('export.ophalen'))}</button></div>`).join('')}`;
}

// Kaart op de instellingenpagina: volledige export.
export async function renderKaart() {
  const el = document.getElementById('exportKaart');
  if (!el) return;
  el.hidden = !heeft('data.export');
  if (el.hidden) return;
  const lijst = await klaarstaand();
  el.innerHTML = `<h3>${esc(t('export.volledig.titel'))}</h3>
    <div class="settings-desc">${esc(t('export.volledig.uitleg'))}</div>
    <button type="button" class="btn btn-default" data-export="volledig"><i class="ti ti-file-zip"></i> ${esc(t('export.aanvragen'))}</button>
    ${klaarstaandHtml(lijst)}`;
  el.onclick = async e => {
    const k = e.target.closest('[data-export]');
    if (!k) return;
    if (k.dataset.export === 'volledig') await aanvragen('volledig');
    if (k.dataset.export === 'ophalen') await ophalen(k.dataset.id);
    await renderKaart();
  };
}
